import { io } from "socket.io-client";
import type { ChatModelRunResult } from "@assistant-ui/react";
import {
  normalizeSafetyExitMetadata,
  safetyExitMetadataFromAssistantText,
  type SafetyExitMetadata,
} from "../safetyExit";
import { buildAssistantContentWithSources } from "./sourceContent";
import {
  applyChatAck,
  applySessionResume,
  buildChatSendPayload,
  buildSocketAuth,
  createSessionIdentity,
  type SocketInitialSessionIdentity,
  type SocketSessionIdentity,
} from "./sessionIdentity";
import { buildSocketTelemetryPayload, recordSocketTurnTelemetry, sendWidgetTelemetry } from "./telemetry";
import { requestGuestSessionBootstrap } from "./guestSessionBootstrap";
import type {
  AgentMetadata,
  ChatAckPayload,
  FollowUpSuggestion,
  GuestSessionBootstrapOptions,
  QualityProfile,
  SessionResumePayload,
  SessionTurnPersistedPayload,
  SocketFactory,
  SocketLike,
  SupervisorHandoffPayload,
  SupervisorLiveEndedPayload,
  SupervisorLiveErrorPayload,
  SupervisorLiveMessageForwardedPayload,
  SupervisorLiveMessagePayload,
  SupervisorLiveReceiptPayload,
  SupervisorLiveTypingPayload,
  SupervisorResponseChunkPayload,
  SupervisorResponseErrorPayload,
  SupervisorResponseFinalPayload,
  SupervisorRoutingStartedPayload,
  SupervisorStageUpdatedPayload,
  SupervisorSuggestionsPayload,
  WidgetConversationSyncPayload,
  WidgetFeedbackSubmitPayload,
  WidgetMessageSyncPayload,
  WidgetPresenceSyncPayload,
  WidgetShellSyncPayload,
  WidgetUnreadSyncPayload,
} from "./types";

type AssistantContent = NonNullable<ChatModelRunResult["content"]>;

type QueueItem =
  | { kind: "chunk"; chunk: string }
  | { kind: "final"; payload: SupervisorResponseFinalPayload }
  | { kind: "error"; error: Error; payload?: SupervisorResponseErrorPayload }
  | { kind: "live_forwarded" }
  | { kind: "abort" };

type TurnCallbacks = {
  onAck?: (payload: ChatAckPayload) => void;
  onRoutingStarted?: (payload: SupervisorRoutingStartedPayload) => void;
  onStageUpdated?: (payload: SupervisorStageUpdatedPayload) => void;
  onError?: (payload: SupervisorResponseErrorPayload) => void;
  onFinal?: (payload: SupervisorResponseFinalPayload) => void;
  onTurnPersisted?: (payload: SessionTurnPersistedPayload) => void;
};

export type SocketChatConnectionOptions = {
  backendUrl: string;
  initialIdentity?: SocketInitialSessionIdentity;
  socketFactory?: SocketFactory;
  guestBootstrap?: GuestSessionBootstrapOptions | false;
  resetGuestOnNextBootstrap?: boolean;
  shouldAcceptSessionResume?: (payload: SessionResumePayload) => boolean;
  onSessionResume?: (payload: SessionResumePayload) => void;
  onSessionIdentityUpdated?: (identity: SocketSessionIdentity) => void;
  onHandoffStarted?: (payload: SupervisorHandoffPayload) => void;
  onHandoffCompleted?: (payload: SupervisorHandoffPayload) => void;
  onHandoffFailed?: (payload: SupervisorHandoffPayload) => void;
  onLiveMessage?: (payload: SupervisorLiveMessagePayload) => void;
  onLiveSystemMessage?: (payload: SupervisorLiveMessagePayload) => void;
  onLiveTyping?: (payload: SupervisorLiveTypingPayload) => void;
  onLiveEnded?: (payload: SupervisorLiveEndedPayload) => void;
  onLiveError?: (payload: SupervisorLiveErrorPayload) => void;
  onLiveReceipt?: (payload: SupervisorLiveReceiptPayload) => void;
  onLiveMessageForwarded?: (payload: SupervisorLiveMessageForwardedPayload) => void;
  onSupervisorRoutingStarted?: (payload: SupervisorRoutingStartedPayload) => void;
  onSupervisorStageUpdated?: (payload: SupervisorStageUpdatedPayload) => void;
  onSupervisorResponseChunk?: (payload: SupervisorResponseChunkPayload) => void;
  onSupervisorResponseFinal?: (payload: SupervisorResponseFinalPayload) => void;
  onSupervisorResponseError?: (payload: SupervisorResponseErrorPayload) => void;
  onSuggestionsReceived?: (clientMessageId: string, suggestions: FollowUpSuggestion[]) => void;
  onTurnPersisted?: (payload: SessionTurnPersistedPayload) => void;
  onWidgetShellSync?: (payload: WidgetShellSyncPayload) => void;
  onWidgetConversationSync?: (payload: WidgetConversationSyncPayload) => void;
  onWidgetMessageSync?: (payload: WidgetMessageSyncPayload) => void;
  onWidgetPresenceSync?: (payload: WidgetPresenceSyncPayload) => void;
  onWidgetUnreadSync?: (payload: WidgetUnreadSyncPayload) => void;
};

export type RunSocketTurnOptions = TurnCallbacks & {
  message: string;
  clientMessageId: string;
  abortSignal: AbortSignal;
  qualityProfile?: QualityProfile;
  modelId?: string;
};

export type ResetSessionOptions = {
  guestId?: string;
  preserveConfiguredGuest?: boolean;
  resetGuestOnNextBootstrap?: boolean;
};

const SOCKET_TRANSPORTS = ["websocket"];
const SOCKET_TIMEOUT_MS = 60_000;
const CONNECT_TIMEOUT_MS = 15_000;
const LIVE_END_ACK_TIMEOUT_MS = 750;
const WIDGET_SYNC_ACK_TIMEOUT_MS = 500;
const MAX_FOLLOW_UP_SUGGESTIONS = 4;
const MAX_SUGGESTION_ID_LENGTH = 64;
const MAX_SUGGESTION_LABEL_LENGTH = 64;
const MAX_SUGGESTION_PROMPT_LENGTH = 200;
const ALLOWED_SUGGESTION_CATEGORIES = new Set([
  "application",
  "admissions",
  "program_comparison",
  "support",
  "transfer_credits",
  "tuition",
]);
const UNSUPPORTED_CAPABILITY_SUGGESTION_PATTERN =
  /\b(schedule|book|reserve|appointment|consultation|live\s+(?:chat|agent)|agent|advisor|adviser|representative|real person)\b/i;
const PLACEHOLDER_SUGGESTION_PATTERN = /\[[^\]]+\]/;
const WEBSITE_OFFLOAD_SUGGESTION_PATTERN =
  /\b(?:phoenix\.edu|search (?:for|on)|visit (?:the )?(?:website|site)|go to (?:the )?(?:website|site)|show me .* on phoenix\.edu)\b/i;
const CONTACT_OFFLOAD_SUGGESTION_PATTERN =
  /\b(?:contact|get in touch with|reach out to|talk to|speak with)\s+(?:our\s+)?(?:enrollment|admissions)\b/i;
const DIRECT_APPLICATION_ACTION_PATTERN =
  /\b(?:start|begin|submit|complete|open)\s+(?:my|your|the|an)?\s*application\b|\b(?:ready to|want to)\s+(?:apply|move forward with (?:an|the)?\s*application)\b|\bapply now\b/i;
const SUGGESTION_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
const HANDOFF_METADATA_PATTERN = /"type"\s*:\s*"handoff"/;

const defaultSocketFactory: SocketFactory = (backendUrl, options) => io(backendUrl, options);

export function sanitizeSupervisorResponseChunk(chunk: string): string {
  const trimmed = chunk.trim();
  if (!trimmed || trimmed === "[handoff]") return "";
  if (trimmed.startsWith("{") && HANDOFF_METADATA_PATTERN.test(trimmed)) return "";
  return chunk.replace(/\[handoff\]/g, "");
}

function createFreshSessionScopeId(prefix: "thread" | "memory"): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return `${prefix}-${cryptoApi.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function payloadMatchesClient(payload: { client_message_id?: string } | undefined, clientMessageId: string): boolean {
  return payload?.client_message_id === clientMessageId;
}

function getFinalResponseText(payload: SupervisorResponseFinalPayload): string {
  return payload.response?.text ?? payload.text ?? "";
}

function getAgentMetadata(payload: SupervisorResponseFinalPayload): AgentMetadata | null {
  return payload.agent_metadata ?? null;
}

function getFollowUpSuggestions(payload: SupervisorResponseFinalPayload): FollowUpSuggestion[] {
  return normalizeFollowUpSuggestions(payload.response?.follow_up_suggestions);
}

function getFinalResponseContent(
  payload: SupervisorResponseFinalPayload,
  finalText: string,
): AssistantContent {
  return buildAssistantContentWithSources(finalText, {
    agentMetadata: getAgentMetadata(payload),
    citations: payload.response?.citations,
  });
}

function getSafetyExitMetadata(payload: SupervisorResponseFinalPayload): SafetyExitMetadata | undefined {
  return normalizeSafetyExitMetadata(getSafetyExitCandidate(getAgentMetadata(payload)));
}

function emitWithAcknowledgementOrTimeout(
  socket: SocketLike,
  event: string,
  payload: unknown,
  timeoutMs: number,
): Promise<void> {
  return new Promise((resolve) => {
    let didResolve = false;
    let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
    const settle = () => {
      if (didResolve) return;
      didResolve = true;
      if (timer) globalThis.clearTimeout(timer);
      resolve();
    };
    timer = globalThis.setTimeout(settle, timeoutMs);

    socket.emit(event, payload, settle);
  });
}

function emitWithPayloadAcknowledgementOrTimeout<T>(
  socket: SocketLike,
  event: string,
  payload: unknown,
  timeoutMs: number,
): Promise<T | null> {
  return new Promise((resolve) => {
    let didResolve = false;
    let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
    const settle = (ackPayload: unknown = null) => {
      if (didResolve) return;
      didResolve = true;
      if (timer) globalThis.clearTimeout(timer);
      resolve(ackPayload as T | null);
    };
    timer = globalThis.setTimeout(() => settle(null), timeoutMs);

    socket.emit(event, payload, settle);
  });
}

export function normalizeFollowUpSuggestions(raw: unknown): FollowUpSuggestion[] {
  if (!Array.isArray(raw)) return [];

  const suggestions: FollowUpSuggestion[] = [];
  const seenIds = new Set<string>();

  for (const value of raw) {
    if (!isRecord(value)) continue;
    const id = cleanSuggestionString(value.id, MAX_SUGGESTION_ID_LENGTH);
    const label = cleanSuggestionString(value.label, MAX_SUGGESTION_LABEL_LENGTH);
    const prompt = cleanSuggestionString(value.prompt, MAX_SUGGESTION_PROMPT_LENGTH);
    const category = cleanSuggestionString(value.category, MAX_SUGGESTION_ID_LENGTH);
    const source = cleanSuggestionString(value.source, MAX_SUGGESTION_ID_LENGTH) || "ai";

    if (!id || !label || !prompt || !category) continue;
    if (!SUGGESTION_ID_PATTERN.test(id)) continue;
    if (!ALLOWED_SUGGESTION_CATEGORIES.has(category)) continue;
    if (source !== "ai") continue;
    const suggestionText = `${id} ${label} ${prompt}`;
    if (UNSUPPORTED_CAPABILITY_SUGGESTION_PATTERN.test(suggestionText)) continue;
    if (PLACEHOLDER_SUGGESTION_PATTERN.test(suggestionText)) continue;
    if (WEBSITE_OFFLOAD_SUGGESTION_PATTERN.test(suggestionText)) continue;
    if (CONTACT_OFFLOAD_SUGGESTION_PATTERN.test(suggestionText)) continue;
    if (DIRECT_APPLICATION_ACTION_PATTERN.test(suggestionText)) continue;
    if (seenIds.has(id)) continue;

    suggestions.push({
      id,
      label,
      prompt,
      category: category as FollowUpSuggestion["category"],
      source,
    });
    seenIds.add(id);

    if (suggestions.length >= MAX_FOLLOW_UP_SUGGESTIONS) break;
  }

  return suggestions;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getSafetyExitCandidate(value: unknown): unknown {
  if (!isRecord(value)) return undefined;
  return value.safetyExit ?? value.safety_exit;
}

function cleanSuggestionString(value: unknown, maxLength: number): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > maxLength) return "";
  return trimmed;
}

export class SocketChatConnection {
  private socket: SocketLike | null = null;
  private connectPromise: Promise<SocketLike> | null = null;
  private guestBootstrapPromise: Promise<void> | null = null;
  private guestBootstrapAbort: AbortController | null = null;
  private connectionGeneration = 0;
  private guestBootstrapCompletedForSocket = false;
  private resetGuestOnNextBootstrap: boolean;
  private identity: SocketSessionIdentity;

  public constructor(private readonly options: SocketChatConnectionOptions) {
    this.identity = createSessionIdentity(options.initialIdentity);
    this.resetGuestOnNextBootstrap = options.resetGuestOnNextBootstrap === true;
  }

  public getIdentity(): SocketSessionIdentity {
    return this.identity;
  }

  public async connect(): Promise<void> {
    await this.ensureConnected();
  }

  public requestSessionResume(): void {
    this.socket?.emit("session.resume.request", {});
  }

  public async publishWidgetShellSync(payload: WidgetShellSyncPayload): Promise<boolean> {
    if (!this.socket?.connected) return false;
    const acknowledgement = await emitWithPayloadAcknowledgementOrTimeout<{ ok?: boolean }>(
      this.socket,
      "widget.shell.sync",
      payload,
      WIDGET_SYNC_ACK_TIMEOUT_MS,
    );
    return acknowledgement?.ok === true;
  }

  public async publishWidgetConversationSync(payload: WidgetConversationSyncPayload): Promise<void> {
    if (!this.socket?.connected) return;
    await emitWithAcknowledgementOrTimeout(
      this.socket,
      "widget.conversation.sync",
      payload,
      WIDGET_SYNC_ACK_TIMEOUT_MS,
    );
  }

  public async publishWidgetMessageSync(payload: WidgetMessageSyncPayload): Promise<void> {
    if (!this.socket?.connected) return;
    await emitWithAcknowledgementOrTimeout(this.socket, "widget.message.sync", payload, WIDGET_SYNC_ACK_TIMEOUT_MS);
  }

  public async publishWidgetPresenceSync(payload: WidgetPresenceSyncPayload): Promise<void> {
    if (!this.socket?.connected) return;
    await emitWithAcknowledgementOrTimeout(this.socket, "widget.presence.sync", payload, WIDGET_SYNC_ACK_TIMEOUT_MS);
  }

  public async publishWidgetUnreadSync(payload: WidgetUnreadSyncPayload): Promise<void> {
    if (!this.socket?.connected) return;
    await emitWithAcknowledgementOrTimeout(this.socket, "widget.unread.sync", payload, WIDGET_SYNC_ACK_TIMEOUT_MS);
  }

  public async publishWidgetFeedbackSubmit(payload: WidgetFeedbackSubmitPayload): Promise<void> {
    if (!this.socket?.connected) return;
    await emitWithAcknowledgementOrTimeout(
      this.socket,
      "widget.feedback.submit",
      payload,
      WIDGET_SYNC_ACK_TIMEOUT_MS,
    );
  }

  public async endLiveSession(reason = "student_requested_end"): Promise<void> {
    const socket = await this.ensureConnected();
    await emitWithAcknowledgementOrTimeout(socket, "chat.live.end", { reason }, LIVE_END_ACK_TIMEOUT_MS);
  }

  public async startNewChat(conversationSync?: WidgetConversationSyncPayload): Promise<SessionResumePayload | null> {
    const socket = await this.ensureConnected();
    const ack = await emitWithPayloadAcknowledgementOrTimeout<{ ok?: boolean; session?: SessionResumePayload }>(
      socket,
      "session.new_chat",
      conversationSync ? { conversation_sync: conversationSync } : {},
      WIDGET_SYNC_ACK_TIMEOUT_MS,
    );
    if (!ack?.ok || !ack.session) return null;
    this.identity = applySessionResume(this.identity, ack.session);
    this.refreshSocketAuth(socket);
    this.options.onSessionIdentityUpdated?.(this.identity);
    this.options.onSessionResume?.(ack.session);
    return ack.session;
  }

  public disconnect(): void {
    this.connectionGeneration += 1;
    this.guestBootstrapAbort?.abort();
    this.guestBootstrapAbort = null;
    this.connectPromise = null;
    this.guestBootstrapPromise = null;
    this.socket?.disconnect();
    this.socket = null;
    this.guestBootstrapCompletedForSocket = false;
  }

  public resetSession(options: ResetSessionOptions = {}): void {
    this.disconnect();
    const preserveConfiguredGuest = options.preserveConfiguredGuest !== false;
    const guestId =
      options.guestId ??
      (preserveConfiguredGuest ? this.identity.guestId ?? this.options.initialIdentity?.guestId : undefined);
    const userToken = this.identity.userToken ?? this.options.initialIdentity?.userToken;
    this.identity = createSessionIdentity({
      guestId,
      threadId: createFreshSessionScopeId("thread"),
      memoryId: createFreshSessionScopeId("memory"),
      userToken,
    });
    if (options.resetGuestOnNextBootstrap === true) {
      this.resetGuestOnNextBootstrap = true;
    }
  }

  public async *runTurn({
    message,
    clientMessageId,
    abortSignal,
    qualityProfile,
    modelId,
    onAck,
    onRoutingStarted,
    onStageUpdated,
    onError,
    onFinal,
    onTurnPersisted,
  }: RunSocketTurnOptions): AsyncGenerator<ChatModelRunResult, void> {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    const socket = await this.ensureConnected();
    if (abortSignal.aborted) return;
    const startedAt = Date.now();
    let firstResponseMs: number | undefined;
    const telemetryPayload = buildSocketTelemetryPayload(clientMessageId);
    sendWidgetTelemetry({
      backendUrl: this.options.backendUrl,
      eventName: "chat_turn_started",
      clientMessageId,
      traceparent: telemetryPayload?.traceparent,
    });

    const queue: QueueItem[] = [];
    let notify: (() => void) | null = null;
    let fullText = "";

    const enqueue = (item: QueueItem) => {
      queue.push(item);
      const wake = notify;
      notify = null;
      wake?.();
    };

    const onChunk = (rawPayload: unknown = {}) => {
      const payload = rawPayload as SupervisorResponseChunkPayload;
      if (!payloadMatchesClient(payload, clientMessageId)) return;
      if (payload.chunk) {
        const chunk = sanitizeSupervisorResponseChunk(payload.chunk);
        if (chunk) enqueue({ kind: "chunk", chunk });
      }
    };
    const onFinalEvent = (rawPayload: unknown = {}) => {
      const payload = rawPayload as SupervisorResponseFinalPayload;
      if (!payloadMatchesClient(payload, clientMessageId)) return;
      enqueue({ kind: "final", payload });
    };
    const onErrorEvent = (rawPayload: unknown = {}) => {
      const payload = rawPayload as SupervisorResponseErrorPayload;
      if (!payloadMatchesClient(payload, clientMessageId)) return;
      onError?.(payload);
      enqueue({ kind: "error", error: new Error(payload.error ?? "Backend error"), payload });
    };
    const onAckEvent = (rawPayload: unknown = {}) => {
      const payload = rawPayload as ChatAckPayload;
      if (!payloadMatchesClient(payload, clientMessageId)) return;
      this.identity = applyChatAck(this.identity, payload);
      this.refreshSocketAuth(socket);
      onAck?.(payload);
      if (payload.status === "live_forwarded") enqueue({ kind: "live_forwarded" });
    };
    const onRoutingStartedEvent = (rawPayload: unknown = {}) => {
      const payload = rawPayload as SupervisorRoutingStartedPayload;
      if (!payloadMatchesClient(payload, clientMessageId)) return;
      onRoutingStarted?.(payload);
    };
    const onStageUpdatedEvent = (rawPayload: unknown = {}) => {
      const payload = rawPayload as SupervisorStageUpdatedPayload;
      if (!payloadMatchesClient(payload, clientMessageId)) return;
      onStageUpdated?.(payload);
    };
    const onTurnPersistedEvent = (rawPayload: unknown = {}) => {
      const payload = rawPayload as SessionTurnPersistedPayload;
      if (!payloadMatchesClient(payload, clientMessageId)) return;
      onTurnPersisted?.(payload);
      const persistedText = payload.turn?.assistant_response?.trim();
      if (!persistedText) return;
      enqueue({
        kind: "final",
        payload: {
          client_message_id: clientMessageId,
          guest_id: payload.turn?.guest_id ?? payload.guest_id,
          thread_id: payload.turn?.thread_id ?? payload.thread_id,
          memory_id: payload.turn?.memory_id ?? payload.memory_id,
          trace_id: payload.turn?.trace_id ?? payload.trace_id,
          quality_profile: payload.quality_profile,
          model_id: payload.model_id,
          response: {
            text: persistedText,
            citations: payload.turn?.citations ?? [],
            follow_up_suggestions: payload.turn?.follow_up_suggestions,
          },
          agent_metadata: payload.turn?.agent_metadata ?? null,
        },
      });
    };
    const onAbort = () => {
      socket.emit("chat.response.cancel", { client_message_id: clientMessageId });
      enqueue({ kind: "abort" });
    };
    const onDisconnect = (reason?: unknown) => {
      enqueue({ kind: "error", error: new Error(`Socket disconnected before final response${reason ? `: ${String(reason)}` : ""}`) });
    };
    const onConnectError = (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err || "connection error");
      enqueue({ kind: "error", error: new Error(message) });
    };

    socket.on("supervisor.response.chunk", onChunk);
    socket.on("supervisor.response.final", onFinalEvent);
    socket.on("supervisor.response.error", onErrorEvent);
    socket.on("chat.ack", onAckEvent);
    socket.on("supervisor.routing.started", onRoutingStartedEvent);
    socket.on("supervisor.stage.updated", onStageUpdatedEvent);
    socket.on("session.turn.persisted", onTurnPersistedEvent);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    abortSignal.addEventListener("abort", onAbort, { once: true });

    try {
      socket.emit(
        "chat.send",
        buildChatSendPayload({
          message: trimmedMessage,
          clientMessageId,
          qualityProfile,
          modelId,
          telemetry: telemetryPayload,
        }),
      );

      while (true) {
        if (abortSignal.aborted) return;
        const item = queue.length > 0
          ? queue.shift()!
          : await new Promise<QueueItem>((resolve) => {
              notify = () => resolve(queue.shift()!);
            });

        if (item.kind === "abort") return;
        if (item.kind === "live_forwarded") {
          sendWidgetTelemetry({
            backendUrl: this.options.backendUrl,
            eventName: "handoff_requested",
            clientMessageId,
            traceparent: telemetryPayload?.traceparent,
            durationMs: Date.now() - startedAt,
          });
          return;
        }
        if (item.kind === "error") throw item.error;
        if (item.kind === "chunk") {
          firstResponseMs ??= Date.now() - startedAt;
          fullText += item.chunk;
          // Filter out [handoff] placeholder text
          const displayText = fullText.replace(/\[handoff\]/g, "").trim();
          if (displayText) {
            yield { content: [{ type: "text", text: displayText }] };
          }
          continue;
        }

        const authoritativeFinalText = getFinalResponseText(item.payload);
        const finalText = (authoritativeFinalText || fullText).replace(/\[handoff\]/g, "").trim();

        // Allow empty responses for specific statuses (e.g., after live chat ends or handoff)
        const allowEmptyStatuses = ["live_forwarded", "ended"];
        const payloadStatus = item.payload.status || item.payload.handoff_status;
        const isAllowedEmpty = (payloadStatus && allowEmptyStatuses.includes(payloadStatus)) || !!item.payload.handoff;

        if (!finalText.trim() && !isAllowedEmpty) {
          console.error("[phoebe-socket] Empty response error - diagnostics:", {
            status: item.payload.status,
            handoff_status: item.payload.handoff_status,
            hasHandoff: !!item.payload.handoff,
            hasResponse: !!item.payload.response,
            traceId: item.payload.trace_id
          });
          throw new Error("Backend returned an empty assistant response.");
        }

        // If empty but allowed, skip rendering this turn
        if (!finalText.trim() && isAllowedEmpty) {
          return;
        }
        onFinal?.(item.payload);
        const responseContent = getFinalResponseContent(item.payload, finalText);
        const safetyExit = getSafetyExitMetadata(item.payload) ?? safetyExitMetadataFromAssistantText(finalText);
        const metadata = {
          custom: {
            agentMetadata: getAgentMetadata(item.payload),
            followUpSuggestions: getFollowUpSuggestions(item.payload),
            ...(safetyExit ? { safetyExit } : {}),
            clientMessageId,
            traceId: item.payload.trace_id,
            guestId: item.payload.guest_id,
            threadId: item.payload.thread_id,
            memoryId: item.payload.memory_id,
            replayed: item.payload.replayed ?? false,
            timingMs: item.payload.timing_ms,
          },
        };
        recordSocketTurnTelemetry({
          clientMessageId,
          traceId: item.payload.trace_id,
        });
        sendWidgetTelemetry({
          backendUrl: this.options.backendUrl,
          eventName: "chat_turn_completed",
          clientMessageId,
          traceId: item.payload.trace_id,
          traceparent: telemetryPayload?.traceparent,
          durationMs: Date.now() - startedAt,
          firstResponseMs,
        });
        yield {
          content: responseContent,
          metadata,
        };
        return;
      }
    } catch (error) {
      sendWidgetTelemetry({
        backendUrl: this.options.backendUrl,
        eventName: "widget_error",
        clientMessageId,
        traceparent: telemetryPayload?.traceparent,
        durationMs: Date.now() - startedAt,
      });
      throw error;
    } finally {
      socket.off("supervisor.response.chunk", onChunk);
      socket.off("supervisor.response.final", onFinalEvent);
      socket.off("supervisor.response.error", onErrorEvent);
      socket.off("chat.ack", onAckEvent);
      socket.off("supervisor.routing.started", onRoutingStartedEvent);
      socket.off("supervisor.stage.updated", onStageUpdatedEvent);
      socket.off("session.turn.persisted", onTurnPersistedEvent);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      abortSignal.removeEventListener("abort", onAbort);
    }
  }

  private refreshSocketAuth(socket: SocketLike | null = this.socket): void {
    if (!socket) return;
    socket.auth = buildSocketAuth(this.identity);
  }

  private ensureSocket(): SocketLike {
    if (this.socket) return this.socket;

    const socket = this.options.socketFactory?.(this.options.backendUrl, {
      transports: SOCKET_TRANSPORTS,
      reconnection: false,
      timeout: SOCKET_TIMEOUT_MS,
      withCredentials: true,
      auth: buildSocketAuth(this.identity),
    }) ?? defaultSocketFactory(this.options.backendUrl, {
      transports: SOCKET_TRANSPORTS,
      reconnection: false,
      timeout: SOCKET_TIMEOUT_MS,
      withCredentials: true,
      auth: buildSocketAuth(this.identity),
    });

    socket.on("session.resume", (rawPayload: unknown = {}) => {
      const payload = rawPayload as SessionResumePayload;
      if (this.options.shouldAcceptSessionResume?.(payload) === false) return;
      this.identity = applySessionResume(this.identity, payload);
      this.refreshSocketAuth(socket);
      this.options.onSessionIdentityUpdated?.(this.identity);
      this.options.onSessionResume?.(payload);
    });
    socket.on("supervisor.handoff.started", (rawPayload: unknown = {}) => {
      this.options.onHandoffStarted?.(rawPayload as SupervisorHandoffPayload);
    });
    socket.on("supervisor.handoff.completed", (rawPayload: unknown = {}) => {
      this.options.onHandoffCompleted?.(rawPayload as SupervisorHandoffPayload);
    });
    socket.on("supervisor.handoff.failed", (rawPayload: unknown = {}) => {
      this.options.onHandoffFailed?.(rawPayload as SupervisorHandoffPayload);
    });
    socket.on("supervisor.live.message", (rawPayload: unknown = {}) => {
      this.options.onLiveMessage?.(rawPayload as SupervisorLiveMessagePayload);
    });
    socket.on("supervisor.live.system", (rawPayload: unknown = {}) => {
      this.options.onLiveSystemMessage?.(rawPayload as SupervisorLiveMessagePayload);
    });
    socket.on("supervisor.live.typing", (rawPayload: unknown = {}) => {
      this.options.onLiveTyping?.(rawPayload as SupervisorLiveTypingPayload);
    });
    socket.on("supervisor.live.ended", (rawPayload: unknown = {}) => {
      this.options.onLiveEnded?.(rawPayload as SupervisorLiveEndedPayload);
    });
    socket.on("supervisor.live.error", (rawPayload: unknown = {}) => {
      this.options.onLiveError?.(rawPayload as SupervisorLiveErrorPayload);
    });
    socket.on("supervisor.live.receipt", (rawPayload: unknown = {}) => {
      this.options.onLiveReceipt?.(rawPayload as SupervisorLiveReceiptPayload);
    });
    socket.on("supervisor.live.message.forwarded", (rawPayload: unknown = {}) => {
      this.options.onLiveMessageForwarded?.(rawPayload as SupervisorLiveMessageForwardedPayload);
    });
    socket.on("supervisor.routing.started", (rawPayload: unknown = {}) => {
      this.options.onSupervisorRoutingStarted?.(rawPayload as SupervisorRoutingStartedPayload);
    });
    socket.on("supervisor.stage.updated", (rawPayload: unknown = {}) => {
      this.options.onSupervisorStageUpdated?.(rawPayload as SupervisorStageUpdatedPayload);
    });
    socket.on("supervisor.response.chunk", (rawPayload: unknown = {}) => {
      this.options.onSupervisorResponseChunk?.(rawPayload as SupervisorResponseChunkPayload);
    });
    socket.on("supervisor.response.final", (rawPayload: unknown = {}) => {
      this.options.onSupervisorResponseFinal?.(rawPayload as SupervisorResponseFinalPayload);
    });
    socket.on("supervisor.response.error", (rawPayload: unknown = {}) => {
      this.options.onSupervisorResponseError?.(rawPayload as SupervisorResponseErrorPayload);
    });
    socket.on("supervisor.suggestions", (rawPayload: unknown = {}) => {
      const payload = rawPayload as SupervisorSuggestionsPayload;
      const suggestions = normalizeFollowUpSuggestions(payload.follow_up_suggestions);
      if (suggestions.length === 0) return;
      if (!payload.client_message_id) {
        console.warn("[phoebe-chat] supervisor.suggestions missing client_message_id");
        return;
      }
      this.options.onSuggestionsReceived?.(payload.client_message_id, suggestions);
    });
    socket.on("session.turn.persisted", (rawPayload: unknown = {}) => {
      this.options.onTurnPersisted?.(rawPayload as SessionTurnPersistedPayload);
    });
    socket.on("widget.shell.synced", (rawPayload: unknown = {}) => {
      this.options.onWidgetShellSync?.(rawPayload as WidgetShellSyncPayload);
    });
    socket.on("widget.conversation.synced", (rawPayload: unknown = {}) => {
      this.options.onWidgetConversationSync?.(rawPayload as WidgetConversationSyncPayload);
    });
    socket.on("widget.message.synced", (rawPayload: unknown = {}) => {
      this.options.onWidgetMessageSync?.(rawPayload as WidgetMessageSyncPayload);
    });
    socket.on("widget.presence.synced", (rawPayload: unknown = {}) => {
      this.options.onWidgetPresenceSync?.(rawPayload as WidgetPresenceSyncPayload);
    });
    socket.on("widget.unread.synced", (rawPayload: unknown = {}) => {
      this.options.onWidgetUnreadSync?.(rawPayload as WidgetUnreadSyncPayload);
    });

    this.socket = socket;
    return socket;
  }

  private async ensureConnected(): Promise<SocketLike> {
    const generation = this.connectionGeneration;
    await this.ensureGuestBootstrapSession();
    if (generation !== this.connectionGeneration) throw new Error("Socket connection cancelled");

    const socket = this.ensureSocket();
    this.refreshSocketAuth(socket);
    if (socket.connected) return socket;
    if (this.connectPromise) return this.connectPromise;

    const pending = this.connectSocketOnce(socket)
      .then((connected) => {
        if (generation !== this.connectionGeneration) {
          connected.disconnect();
          throw new Error("Socket connection cancelled");
        }
        return connected;
      })
      .catch(async (error) => {
        if (generation !== this.connectionGeneration) throw error;
        if (!this.shouldRetryGuestConnection()) throw error;
        await this.ensureGuestBootstrapSession(true);
        if (generation !== this.connectionGeneration) throw new Error("Socket connection cancelled");
        this.refreshSocketAuth(socket);
        const connected = await this.connectSocketOnce(socket);
        if (generation !== this.connectionGeneration) {
          connected.disconnect();
          throw new Error("Socket connection cancelled");
        }
        return connected;
      })
      .finally(() => {
        if (this.connectPromise === pending) this.connectPromise = null;
      });

    this.connectPromise = pending;

    return this.connectPromise;
  }

  private connectSocketOnce(socket: SocketLike): Promise<SocketLike> {
    return new Promise<SocketLike>((resolve, reject) => {
      const timer = globalThis.setTimeout(() => {
        reject(new Error("Socket connection timed out after 15s"));
      }, CONNECT_TIMEOUT_MS);

      const cleanup = () => {
        globalThis.clearTimeout(timer);
        socket.off("connect", handleConnect);
        socket.off("connect_error", handleConnectError);
      };

      const handleConnect = () => {
        cleanup();
        socket.io?.reconnection(true);
        resolve(socket);
      };
      const handleConnectError = (err: unknown) => {
        cleanup();
        reject(err instanceof Error ? err : new Error(String(err || "connection error")));
      };

      socket.once("connect", handleConnect);
      socket.once("connect_error", handleConnectError);
      socket.connect?.();
    });
  }

  private async ensureGuestBootstrapSession(force = false): Promise<void> {
    if (!this.isGuestBootstrapEnabled() || this.identity.userToken) return;
    if (!force && this.guestBootstrapCompletedForSocket) return;
    if (this.guestBootstrapPromise) return this.guestBootstrapPromise;

    const startedAt = Date.now();
    const generation = this.connectionGeneration;
    const abort = new AbortController();
    this.guestBootstrapAbort = abort;
    const pending = requestGuestSessionBootstrap({
      backendUrl: this.options.backendUrl,
      guestId: this.identity.guestId,
      resetGuest: this.resetGuestOnNextBootstrap,
      signal: abort.signal,
      options: this.guestBootstrapOptions(),
    })
      .then((result) => {
        if (generation !== this.connectionGeneration) return;
        this.identity = {
          ...this.identity,
          guestId: result.guestId ?? this.identity.guestId,
        };
        this.options.onSessionIdentityUpdated?.(this.identity);
        this.guestBootstrapCompletedForSocket = true;
        this.resetGuestOnNextBootstrap = false;
        sendWidgetTelemetry({
          backendUrl: this.options.backendUrl,
          eventName: "guest_session_bootstrap_succeeded",
          durationMs: Date.now() - startedAt,
        });
      })
      .catch((error) => {
        if (generation === this.connectionGeneration) sendWidgetTelemetry({
          backendUrl: this.options.backendUrl,
          eventName: "widget_error",
          durationMs: Date.now() - startedAt,
        });
        throw error;
      })
      .finally(() => {
        if (this.guestBootstrapPromise === pending) {
          this.guestBootstrapPromise = null;
          this.guestBootstrapAbort = null;
        }
      });

    this.guestBootstrapPromise = pending;
    return pending;
  }

  private shouldRetryGuestConnection(): boolean {
    return this.isGuestBootstrapEnabled() && !this.identity.userToken;
  }

  private isGuestBootstrapEnabled(): boolean {
    const option = this.options.guestBootstrap;
    if (option === false || option?.enabled === false) return false;
    if (option !== undefined) return true;
    return false;
  }

  private guestBootstrapOptions(): GuestSessionBootstrapOptions {
    const option = this.options.guestBootstrap;
    return option && option !== false ? option : {};
  }
}
